import React, { useState, useEffect, useCallback } from 'react';
import { Mail, ChevronRight, RotateCcw, Save, Check, AlertCircle, Tag, Eye, X, Wrench, Truck, MapPin, BookUser, Activity } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';
import { useSociete } from '../../context/SocieteContext';

// Thèmes avec couleur et icône
const THEME_CONFIG = {
  Annuaire:     { color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', icon: BookUser, label: 'Annuaire' },
  Distribution: { color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', icon: Truck,    label: 'Distribution' },
  Location:     { color: '#D97706', bg: '#FFF7ED', border: '#FDE68A', icon: MapPin,   label: 'Location' },
  Réparation:   { color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', icon: Wrench,   label: 'Réparation' },
  Maintenance:  { color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC', icon: Activity, label: 'Maintenance' },
};

const THEME_ORDER = ['Annuaire', 'Distribution', 'Location', 'Réparation', 'Maintenance'];

const DEFAULT_COLOR = { bg: '#F8FAFC', border: '#E2E8F0', icon: '#64748B', badge: '#F1F5F9', badgeText: '#475569' };

// Données fictives pour la prévisualisation
const FAKE_VARS = {
  nom_client:         'Dupont Construction',
  numero_devis:       'DEV-2026-0042',
  numero_facture:     'FAC-2026-0042',
  date_facture:       '15/04/2026',
  nom_emetteur:       'Jean Martin',
  date_devis:         '15/04/2026',
  date_releve:        '03/07/2026',
  total_ttc:          '4 850,00 €',
  nom:                'Sophie Leclerc',
  lien:               'https://bedee.app/exemple-lien',
  org_nom:            'MBud SAS',
  inviteur:           'Jean Martin',
  numero_contrat:     'CL-2026-0018',
  numero_or:          'OR-2026-0007',
  signataire_nom:     'Paul Dubois',
  date_signature:     '02/07/2026 à 14h30',
  // Maintenance préventive
  client_nom:         'Dupont Construction',
  lien_optout:        'https://bedee.app/optout/exemple',
  tableau_materiels:  `<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;margin:16px 0;">
    <thead><tr style="background:#F8FAFC;">
      <th style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #E2E8F0;">Matériel</th>
      <th style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #E2E8F0;">N° Série</th>
      <th style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #E2E8F0;">Entretien prévu</th>
      <th style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #E2E8F0;">Échéance</th>
    </tr></thead>
    <tbody>
      <tr><td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;font-size:14px;color:#0F172A;"><div style="font-weight:600;">Chargeuse XL300</div><div style="font-size:12px;color:#64748B;margin-top:2px;">Caterpillar 950GC</div></td><td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;font-size:13px;color:#475569;">SN-0042-CAT</td><td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;font-size:13px;color:#475569;">Vidange moteur</td><td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;font-size:13px;color:#0F172A;">395&#8239;/&#8239;450 h — dans 8 jours</td></tr>
      <tr><td style="padding:10px 14px;font-size:14px;color:#0F172A;"><div style="font-weight:600;">Grue mobile RT600</div><div style="font-size:12px;color:#64748B;margin-top:2px;">Liebherr LTM 1050</div></td><td style="padding:10px 14px;font-size:13px;color:#475569;">SN-0087-LTM</td><td style="padding:10px 14px;font-size:13px;color:#475569;">Contrôle freins</td><td style="padding:10px 14px;font-size:13px;color:#0F172A;"><span style="color:#DC2626;font-weight:600;">1&#8239;200 / 1&#8239;200 h — EN RETARD</span></td></tr>
    </tbody>
  </table>`,
};

function applyFakeVars(text) {
  if (!text) return '';
  return Object.entries(FAKE_VARS).reduce(
    (t, [k, v]) => t.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v),
    text
  );
}

function buildEmailHtml(sujet, corps, logoUrl) {
  const bodyHtml = applyFakeVars(corps).replace(/\n/g, '<br/>');

  // Reproduit exactement la logique de _email_wrapper dans email_service.py
  const isCustomLogo = !!logoUrl;
  const headerBg = isCustomLogo
    ? 'background:white;'
    : 'background:linear-gradient(135deg,#1A3A6E 0%,#2563EB 100%);';
  const logoSize = isCustomLogo ? '120' : '36';
  const logoStyle = isCustomLogo
    ? 'display:block;max-height:48px;width:auto;border:0;'
    : 'display:block;border-radius:8px;border:0;';
  const logoTag = isCustomLogo
    ? `<img src="${logoUrl}" alt="Logo" width="${logoSize}" height="auto" style="${logoStyle}" />`
    : '';
  const brandName = isCustomLogo
    ? ''
    : '<span style="color:white;font-size:20px;font-weight:700;letter-spacing:-0.5px;">Bedee</span>';

  return `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F1F5F9;font-family:'Helvetica Neue',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F1F5F9;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">
        <tr>
          <td style="${headerBg}padding:24px 32px;border-radius:12px 12px 0 0;text-align:left;">
            <table cellpadding="0" cellspacing="0">
              <tr>
                <td style="padding-right:12px;vertical-align:middle;">${logoTag}</td>
                <td style="vertical-align:middle;">${brandName}</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="background:white;padding:36px 32px;border-radius:0 0 12px 12px;">
            <h1 style="margin:0 0 16px;color:#0F172A;font-size:22px;font-weight:700;">${applyFakeVars(sujet)}</h1>
            <div style="color:#334155;font-size:15px;line-height:1.8;">${bodyHtml}</div>
          </td>
        </tr>
        <tr>
          <td style="padding:20px 32px;text-align:center;">
            <p style="margin:0;color:#94A3B8;font-size:12px;">
              Bedee · Gestion de parc matériel<br>
              Cet email a été envoyé automatiquement, merci de ne pas y répondre.
            </p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function PreviewModal({ sujet, corps, logoUrl, onClose }) {
  const html = buildEmailHtml(sujet, corps, logoUrl);
  const renderedSubject = applyFakeVars(sujet);
  const iframeRef = React.useRef(null);

  const handleIframeLoad = React.useCallback(() => {
    const frame = iframeRef.current;
    if (frame?.contentDocument?.documentElement) {
      const h = frame.contentDocument.documentElement.scrollHeight;
      frame.style.height = (h + 8) + 'px';
    }
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl mx-auto my-8 rounded-2xl shadow-2xl flex flex-col"
        style={{ background: '#ffffff' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: '#E2E8F0' }}>
          <div className="flex items-center gap-2">
            <Eye size={15} style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>
              Prévisualisation email
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid="preview-close-btn"
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100"
          >
            <X size={14} style={{ color: '#64748B' }} />
          </button>
        </div>

        {/* Sujet */}
        <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: '#E2E8F0', background: '#F8FAFC' }}>
          <span className="text-xs font-semibold" style={{ color: '#64748B' }}>Sujet :</span>
          <span className="text-sm font-medium" style={{ color: '#0F172A' }}>{renderedSubject}</span>
        </div>

        {/* Note données fictives */}
        <div className="px-5 py-2 flex items-center gap-1.5 border-b" style={{ borderColor: '#E2E8F0', background: '#FFFBEB' }}>
          <AlertCircle size={12} style={{ color: '#B45309' }} />
          <span className="text-xs" style={{ color: '#92400E' }}>
            Aperçu avec données fictives — les valeurs réelles sont substituées à l'envoi.
          </span>
        </div>

        {/* Iframe auto-height, sans scrollbar interne */}
        <iframe
          ref={iframeRef}
          title="Aperçu email"
          srcDoc={html}
          onLoad={handleIframeLoad}
          data-testid="preview-iframe"
          style={{ width: '100%', height: '500px', border: 'none', display: 'block', borderRadius: '0 0 16px 16px' }}
          sandbox="allow-same-origin"
          scrolling="no"
        />
      </div>
    </div>
  );
}

function VariableTag({ variable, onInsert }) {
  return (
    <button
      type="button"
      onClick={() => onInsert(variable.cle)}
      data-testid={`var-tag-${variable.cle}`}
      title={variable.description}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono transition-all hover:scale-105 active:scale-95 cursor-pointer border"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border)',
        color: 'var(--primary)',
      }}
    >
      <Tag size={10} />
      {`{{${variable.cle}}}`}
    </button>
  );
}

export default function ModeleEmails({ mode = 'commercial' }) {
  const isSystemeMode = mode === 'systeme';
  const [contextes, setContextes] = useState([]);
  const [templates, setTemplates] = useState({});
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ sujet: '', corps_html: '' });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const sujetRef = React.useRef(null);
  const corpsRef = React.useRef(null);
  const lastFocusedField = React.useRef('corps_html');

  // Logo de l'agence active pour la prévisualisation
  const { activeAgences } = useSociete();
  useEffect(() => {
    const agenceId = activeAgences[0]?.agence_id;
    if (!agenceId) { setLogoUrl(null); return; }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${backendUrl}/api/public/agence-logo/${agenceId}`;
    // Utiliser Image() — plus fiable que fetch HEAD pour tester la disponibilité
    const img = new window.Image();
    img.onload = () => setLogoUrl(url);
    img.onerror = () => setLogoUrl(null);
    img.src = url;
  }, [activeAgences]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ctxRes, tmplRes] = await Promise.all([
        isSystemeMode ? api.getEmailContextesSysteme() : api.getEmailContextes(),
        api.getEmailTemplates(),
      ]);
      setContextes(ctxRes.data);
      const tmplMap = {};
      for (const t of tmplRes.data) {
        tmplMap[t.contexte] = t;
      }
      setTemplates(tmplMap);
    } catch (e) {
      toast.error('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectContexte = (ctx) => {
    setSelected(ctx);
    const existing = templates[ctx.contexte];
    setForm({
      sujet: existing?.sujet || ctx.sujet_defaut || '',
      corps_html: existing?.corps_html || ctx.corps_defaut || '',
    });
  };

  const insertVar = (fieldRef, fieldName, cle) => {
    const el = fieldRef.current;
    if (!el) {
      setForm(f => ({ ...f, [fieldName]: f[fieldName] + `{{${cle}}}` }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const newVal = el.value.slice(0, start) + `{{${cle}}}` + el.value.slice(end);
    setForm(f => ({ ...f, [fieldName]: newVal }));
    setTimeout(() => {
      el.focus();
      const pos = start + `{{${cle}}}`.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!form.sujet.trim()) { toast.error('Le sujet est requis'); return; }
    if (!form.corps_html.trim()) { toast.error('Le corps est requis'); return; }
    setSaving(true);
    try {
      if (isSystemeMode) {
        await api.upsertEmailTemplateSysteme(selected.contexte, {
          contexte: selected.contexte,
          sujet: form.sujet,
          corps_html: form.corps_html,
        });
        // Mettre à jour sujet_defaut/corps_defaut dans la liste des contextes
        setContextes(prev => prev.map(c =>
          c.contexte === selected.contexte
            ? { ...c, sujet_defaut: form.sujet, corps_defaut: form.corps_html }
            : c
        ));
        setSelected(prev => ({ ...prev, sujet_defaut: form.sujet, corps_defaut: form.corps_html }));
      } else {
        await api.upsertEmailTemplate(selected.contexte, {
          contexte: selected.contexte,
          sujet: form.sujet,
          corps_html: form.corps_html,
        });
        setTemplates(prev => ({
          ...prev,
          [selected.contexte]: { ...prev[selected.contexte], sujet: form.sujet, corps_html: form.corps_html, is_custom: true }
        }));
      }
      toast.success('Modèle enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!templates[selected.contexte]) {
      // Pas de custom template, juste remettre les valeurs par défaut dans le form
      setForm({ sujet: selected.sujet_defaut || '', corps_html: selected.corps_defaut || '' });
      return;
    }
    setResetting(true);
    try {
      await api.deleteEmailTemplate(selected.contexte);
      toast.success('Modèle réinitialisé au défaut');
      setTemplates(prev => {
        const next = { ...prev };
        delete next[selected.contexte];
        return next;
      });
      setForm({ sujet: selected.sujet_defaut || '', corps_html: selected.corps_defaut || '' });
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  const colors = selected ? (THEME_CONFIG[selected.theme] || DEFAULT_COLOR) : DEFAULT_COLOR;
  const themeColor = colors.color || colors.icon || '#64748B';
  const isCustom = selected && !!templates[selected.contexte];
  const hasChanges = selected && (
    form.sujet !== (templates[selected.contexte]?.sujet || selected.sujet_defaut || '') ||
    form.corps_html !== (templates[selected.contexte]?.corps_html || selected.corps_defaut || '')
  );

  // Grouper les contextes par thème (mode commercial uniquement)
  const contextesParTheme = contextes.reduce((acc, ctx) => {
    const t = ctx.theme || 'Autres';
    if (!acc[t]) acc[t] = [];
    acc[t].push(ctx);
    return acc;
  }, {});
  const THEME_ORDER_LOCAL = ['Annuaire', 'Distribution', 'Location', 'Réparation', 'Maintenance'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: 'var(--primary)' }} />
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full min-h-0" style={{ minHeight: '600px' }}>

      {/* ─── Liste des contextes ───────────────────────────────────────── */}
      <aside className="w-72 flex-shrink-0 flex flex-col gap-1 overflow-y-auto">
        <div className="mb-3">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {isSystemeMode ? 'Emails système' : 'Modèles d\'emails'}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {isSystemeMode
              ? 'Personnalisez les emails automatiques envoyés par l\'application.'
              : 'Personnalisez vos messages pour chaque situation.'}
          </p>
        </div>

        {(isSystemeMode ? [{ theme: 'Système', ctxs: contextes }]
        : THEME_ORDER_LOCAL.filter(t => contextesParTheme[t]).map(t => ({ theme: t, ctxs: contextesParTheme[t] }))
        ).map(({ theme, ctxs }) => {
          const tc = THEME_CONFIG[theme] || {};
          const ThemeIcon = tc.icon || Mail;
          return (
            <div key={theme} className="mb-2">
              {/* En-tête thème */}
              <div className="flex items-center gap-1.5 px-1 py-1 mb-1">
                <div className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ background: tc.bg || '#F8FAFC' }}>
                  <ThemeIcon size={11} style={{ color: tc.color || '#64748B' }} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: tc.color || 'var(--text-muted)', letterSpacing: '0.04em' }}>
                  {theme}
                </span>
              </div>
              {ctxs.map(ctx => {
                const tc2 = THEME_CONFIG[ctx.theme] || {};
                const isActive = selected?.contexte === ctx.contexte;
                const isModified = !!templates[ctx.contexte];
                return (
                  <button
                    key={ctx.contexte}
                    type="button"
                    onClick={() => selectContexte(ctx)}
                    data-testid={`ctx-card-${ctx.contexte}`}
                    className="w-full text-left rounded-xl p-3 border transition-all mb-1"
                    style={{
                      background: isActive ? (tc2.bg || '#F8FAFC') : 'var(--surface-1)',
                      borderColor: isActive ? (tc2.border || '#E2E8F0') : 'var(--border)',
                      boxShadow: isActive ? `0 0 0 2px ${tc2.border || '#E2E8F0'}` : 'none',
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: isActive ? (tc2.bg || '#F8FAFC') : 'var(--surface-2)' }}>
                          <Mail size={13} style={{ color: tc2.color || '#64748B' }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                            {ctx.label}
                          </p>
                          {isModified && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5 mt-0.5"
                              style={{ background: tc2.bg || '#F1F5F9', color: tc2.color || '#475569', fontSize: '10px' }}>
                              <Check size={9} /> Personnalisé
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </aside>

      {/* ─── Éditeur ──────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full py-24 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'var(--surface-2)' }}>
              <Mail size={24} style={{ color: 'var(--text-muted)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Sélectionnez un contexte pour modifier son modèle
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">

            {/* Header du contexte */}
            <div className="rounded-xl p-4 border flex items-start gap-3"
              style={{ background: colors.bg || '#F8FAFC', borderColor: colors.border || '#E2E8F0' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: '#fff', border: `1.5px solid ${colors.border || '#E2E8F0'}` }}>
                <Mail size={16} style={{ color: themeColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                    {selected.label}
                  </h3>
                  {isCustom ? (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: colors.bg || '#F1F5F9', color: themeColor }}>
                      Personnalisé
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>
                      Défaut
                    </span>
                  )}
                  {hasChanges && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: '#FEF3C7', color: '#B45309' }}>
                      Modifié
                    </span>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {selected.description}
                </p>
              </div>
            </div>

            {/* Variables disponibles */}
            {selected.variables.length > 0 && (
              <div className="rounded-xl p-4 border" style={{ background: 'var(--surface-1)', borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Tag size={13} style={{ color: 'var(--primary)' }} />
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    Variables disponibles — cliquez pour insérer
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.variables.map(v => (
                    <VariableTag
                      key={v.cle}
                      variable={v}
                      onInsert={(cle) => {
                        insertVar(
                          lastFocusedField.current === 'sujet' ? sujetRef : corpsRef,
                          lastFocusedField.current === 'sujet' ? 'sujet' : 'corps_html',
                          cle
                        );
                      }}
                    />
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--text-disabled)' }}>
                  Placez le curseur dans le champ désiré avant de cliquer sur une variable.
                </p>
              </div>
            )}

            {/* Champ Sujet */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                Sujet de l'email
              </label>
              <input
                ref={sujetRef}
                type="text"
                value={form.sujet}
                onFocus={() => { lastFocusedField.current = 'sujet'; }}
                onChange={e => setForm(f => ({ ...f, sujet: e.target.value }))}
                data-testid="template-sujet"
                placeholder={selected.sujet_defaut}
                className="rounded-lg px-3 py-2 text-sm border transition-all"
                style={{
                  background: 'var(--surface-1)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* Champ Corps */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                Corps de l'email
                <span className="ml-1.5 font-normal" style={{ color: 'var(--text-disabled)' }}>
                  (texte brut ou HTML simple)
                </span>
              </label>
              <textarea
                ref={corpsRef}
                value={form.corps_html}
                onFocus={() => { lastFocusedField.current = 'corps_html'; }}
                onChange={e => setForm(f => ({ ...f, corps_html: e.target.value }))}
                data-testid="template-corps"
                placeholder={selected.corps_defaut}
                rows={10}
                className="rounded-lg px-3 py-2.5 text-sm border transition-all font-mono resize-y"
                style={{
                  background: 'var(--surface-1)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                  minHeight: '180px',
                }}
              />
              <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                Les sauts de ligne sont convertis en &lt;br/&gt; dans l'email final.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="template-save-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
                style={{ background: 'var(--primary-gradient)', color: '#fff' }}
              >
                {saving ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Enregistrer
              </button>

              <button
                type="button"
                onClick={() => setShowPreview(true)}
                data-testid="template-preview-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all"
                style={{
                  background: 'var(--surface-1)',
                  borderColor: colors.border || '#E2E8F0',
                  color: themeColor,
                }}
              >
                <Eye size={13} />
                Prévisualiser
              </button>

              {!isSystemeMode && (
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                data-testid="template-reset-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all disabled:opacity-60"
                style={{
                  background: 'var(--surface-1)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-secondary)',
                }}
              >
                <RotateCcw size={13} />
                Remettre par défaut
              </button>
              )}

              {!isSystemeMode && !isCustom && !hasChanges && (
                <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <AlertCircle size={12} />
                  Modèle par défaut actif
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* Modale de prévisualisation */}
      {showPreview && selected && (
        <PreviewModal
          sujet={form.sujet || selected.sujet_defaut}
          corps={form.corps_html || selected.corps_defaut}
          logoUrl={logoUrl}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
